/**
 * Extrae texto de un PDF en el navegador (pdf.js).
 * Útil para rellenar el guion sin copiar/pegar.
 */
import * as pdfjsLib from 'pdfjs-dist/build/pdf';

// Worker CDN alineado a la versión instalada (evita líos con webpack/worker).
pdfjsLib.GlobalWorkerOptions.workerSrc = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjsLib.version}/pdf.worker.min.js`;

const MAX_PAGES = 80;
const MAX_CHARS = 120_000;

/**
 * @param {ArrayBuffer|Uint8Array} data
 * @returns {Promise<{ text: string, pages: number, truncated: boolean }>}
 */
export async function extractTextFromPdfData(data) {
  const loadingTask = pdfjsLib.getDocument({ data, useSystemFonts: true });
  const pdf = await loadingTask.promise;
  const totalPages = pdf.numPages || 0;
  const limit = Math.min(totalPages, MAX_PAGES);
  const parts = [];
  let truncated = totalPages > MAX_PAGES;

  for (let i = 1; i <= limit; i += 1) {
    const page = await pdf.getPage(i);
    const content = await page.getTextContent();
    const line = (content.items || [])
      .map((item) => (item && typeof item.str === 'string' ? item.str : ''))
      .join(' ')
      .replace(/[ \t]+/g, ' ')
      .trim();
    if (line) parts.push(line);
  }

  let text = parts.join('\n\n').replace(/\n{3,}/g, '\n\n').trim();
  if (text.length > MAX_CHARS) {
    text = text.slice(0, MAX_CHARS);
    truncated = true;
  }
  return { text, pages: totalPages, truncated };
}

/**
 * @param {File} file
 * @returns {Promise<{ text: string, pages: number, truncated: boolean, fileName: string }>}
 */
export async function extractTextFromPdfFile(file) {
  if (!file) throw new Error('No se seleccionó archivo.');
  const name = String(file.name || '').toLowerCase();
  const isPdf = file.type === 'application/pdf' || name.endsWith('.pdf');
  if (!isPdf) throw new Error('Solo se aceptan archivos PDF.');
  if (file.size > 25 * 1024 * 1024) {
    throw new Error('El PDF supera 25 MB. Usa un archivo más liviano.');
  }
  const buffer = await file.arrayBuffer();
  const result = await extractTextFromPdfData(new Uint8Array(buffer));
  if (!result.text || result.text.length < 20) {
    throw new Error(
      'No se pudo leer texto del PDF (puede ser escaneado/imagen). Prueba pegar el guion o un PDF con texto seleccionable.',
    );
  }
  return { ...result, fileName: file.name || 'guion.pdf' };
}
